import { useCallback, useState } from 'react';
import { useStore } from '../store';
import type { SitemapResult } from '../types';

export function DropZone() {
  const setData = useStore((s) => s.setData);
  const setError = useStore((s) => s.setError);
  const loadError = useStore((s) => s.loadError);
  const openCommandBuilder = useStore((s) => s.openCommandBuilder);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      try {
        const text = await file.text();
        const json = JSON.parse(text) as SitemapResult;
        if (!json.pages || !Array.isArray(json.pages)) {
          throw new Error('pages 配列が見つかりません');
        }
        setData(json, file.name);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(`読み込み失敗: ${message}`);
      }
    },
    [setData, setError],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-8">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`w-full max-w-2xl rounded-xl border-2 border-dashed p-16 text-center transition ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-white hover:border-slate-400'
        }`}
      >
        <h1 className="mb-2 flex items-center justify-center gap-2 text-2xl font-bold text-slate-800">
          Sitemap Viewer
          <span
            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
            title="開発中の機能を含みます。仕様や見た目は予告なく変わることがあります"
          >
            Beta
          </span>
        </h1>
        <p className="mb-8 text-sm text-slate-600">
          4ge-sitemap で生成した JSON を読み込んで可視化します
        </p>
        <div className="mb-6">
          <svg
            className="mx-auto h-16 w-16 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
        </div>
        <p className="mb-4 text-slate-700">
          JSON ファイルをここにドラッグ &amp; ドロップ
        </p>
        <p className="mb-4 text-xs text-slate-500">または</p>
        <label className="inline-block cursor-pointer rounded-lg bg-slate-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-slate-700">
          ファイルを選択
          <input
            type="file"
            accept="application/json,.json"
            onChange={onChange}
            className="hidden"
          />
        </label>
        {loadError && (
          <p className="mt-6 rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">
            {loadError}
          </p>
        )}
        <p className="mt-8 text-xs text-slate-500">
          🔒 データはあなたのブラウザ内でのみ処理されます。
          <br />
          サーバーへの保存や送信は一切行われませんのでご安心ください。
        </p>
      </div>

      {/* JSON 未取得ユーザー向けの導線。枠（ドロップゾーン）の外に置く */}
      <div className="mt-6 w-full max-w-2xl text-center">
        <p className="mb-2 text-xs text-slate-500">
          サイトをクロールして JSON を作るところから始める
        </p>
        <button
          onClick={openCommandBuilder}
          className="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100"
        >
          使い方・ターミナル用のコマンド生成
        </button>
      </div>
    </div>
  );
}
